import React, { useState, useEffect } from 'react';
import { loadClients, loadUsers } from '../utils/storage';

const MetaNoContactForm = ({ record, onSave, onClose, currentUser }) => {
  const [formData, setFormData] = useState({
    mes: '',
    seguimiento: '',
    fecha_asunto: '',
    nombre_cliente: '',
    cuenta_publicitaria: '',
    celula: '',
    campana: '',
    asunto: '',
    acciones: '',
    status: 'pendiente'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [availableCells, setAvailableCells] = useState([]);

  // Cargar clientes y usuarios al montar el componente
  useEffect(() => {
    const loadData = async () => {
      try {
        // Cargar clientes
        setLoadingClients(true);
        const clientsData = await loadClients();
        setClients(clientsData || []);
        
        // Cargar usuarios/agentes
        setLoadingUsers(true);
        const usersData = await loadUsers();
        setUsers(usersData || []);
        
        // Extraer células únicas de los clientes y usuarios
        const cellsFromClients = clientsData?.flatMap(client => 
          client.cells || (client.cell ? client.cell.split(',').map(c => c.trim()) : [])
        ) || [];
        
        const cellsFromUsers = usersData?.flatMap(user => 
          user.cells || []
        ) || [];
        
        const allCells = [...new Set([...cellsFromClients, ...cellsFromUsers])].sort();
        setAvailableCells(allCells);
        
      } catch (error) {
        console.error('Error al cargar datos:', error);
        setClients([]);
        setUsers([]);
        setAvailableCells([]);
      } finally {
        setLoadingClients(false);
        setLoadingUsers(false);
      }
    };
    
    loadData();
  }, []);

  useEffect(() => {
    if (record) {
      // Formatear la fecha para el input date
      const fechaFormateada = record.fecha_asunto 
        ? new Date(record.fecha_asunto).toISOString().split('T')[0]
        : '';
      
      setFormData({
        mes: record.mes || '',
        seguimiento: record.seguimiento || '',
        fecha_asunto: fechaFormateada,
        nombre_cliente: record.nombre_cliente || '',
        cuenta_publicitaria: record.cuenta_publicitaria || '',
        celula: record.celula || '',
        campana: record.campana || '',
        asunto: record.asunto || '',
        acciones: record.acciones || '',
        status: record.status || 'pendiente'
      });
      
      // Establecer el término de búsqueda del cliente
      setClientSearchTerm(record.nombre_cliente || '');
    } else {
      // Para nuevos registros, establecer valores por defecto
      const currentDate = new Date().toISOString().split('T')[0];
      const currentMonth = new Date().toLocaleDateString('es-ES', { 
        month: 'long'
      });
      
      setFormData(prev => ({
        ...prev,
        fecha_asunto: currentDate,
        mes: currentMonth.charAt(0).toUpperCase() + currentMonth.slice(1),
        seguimiento: currentUser?.firstName || currentUser?.name || currentUser?.email || ''
      }));
    }
  }, [record, currentUser]);

  // Filtrar clientes basado en el término de búsqueda
  useEffect(() => {
    if (clientSearchTerm.trim()) {
      const filtered = clients.filter(client =>
        client.name.toLowerCase().includes(clientSearchTerm.toLowerCase())
      );
      setFilteredClients(filtered);
    } else {
      setFilteredClients([]);
    }
  }, [clientSearchTerm, clients]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.mes.trim()) {
      newErrors.mes = 'El mes es requerido';
    }

    if (!formData.seguimiento.trim()) {
      newErrors.seguimiento = 'El seguimiento es requerido';
    }

    if (!formData.fecha_asunto) {
      newErrors.fecha_asunto = 'La fecha del asunto es requerida';
    }

    if (!formData.nombre_cliente.trim()) {
      newErrors.nombre_cliente = 'El nombre del cliente es requerido';
    }

    if (!formData.cuenta_publicitaria.trim()) {
      newErrors.cuenta_publicitaria = 'La cuenta publicitaria es requerida';
    }

    if (!formData.celula.trim()) {
      newErrors.celula = 'La célula es requerida';
    }

    if (!formData.campana.trim()) {
      newErrors.campana = 'La campaña es requerida';
    }

    if (!formData.asunto.trim()) {
      newErrors.asunto = 'El asunto es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error al guardar:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const mesesOptions = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Usar células dinámicas o fallback a células predeterminadas
  const celulasOptions = availableCells.length > 0 ? availableCells : [
    'C1GA', 'C2GA', 'C3GA', 'C4GA', 'C5GA', 'C6GA', 'C7GA',
    'C1MA', 'C2MA', 'C3MA', 'C4MA',
    'G1M1', 'G1M2', 'G1M3', 'G1M4',
    'G2M1', 'G2M2', 'G2M3', 'G2M4',
    'G3M1', 'G3M2', 'G3M3', 'G3M4',
    'G4M1', 'G4M2', 'G4M3', 'G4M4',
    'G5M1', 'G5M2', 'G5M3', 'G5M4',
    'G6M1', 'G6M2', 'G6M3', 'G6M4'
  ];

  const statusOptions = [
    { value: 'pendiente', label: '⏳ Pendiente', color: 'text-yellow-600' },
    { value: 'en_proceso', label: '🔄 En Proceso', color: 'text-blue-600' },
    { value: 'completado', label: '✅ Completado', color: 'text-green-600' },
    { value: 'cancelado', label: '❌ Cancelado', color: 'text-red-600' }
  ];

  // Función para generar un nombre amigable desde firstName y lastName
  const getDisplayName = (user) => {
    // Prioridad 1: firstName + lastName
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    
    // Prioridad 2: Solo firstName
    if (user.firstName && user.firstName.trim()) {
      return user.firstName;
    }
    
    // Prioridad 3: Solo lastName
    if (user.lastName && user.lastName.trim()) {
      return user.lastName;
    }
    
    // Prioridad 4: Campo name (por compatibilidad)
    if (user.name && user.name.trim()) {
      return user.name;
    }
    
    // Prioridad 5: Generar desde email
    const emailPart = user.email.split('@')[0];
    
    // Mapeo específico para emails conocidos
    const emailMappings = {
      'admin': 'Admin User',
      'gcarvajalvidal': 'Gonzalo Carvajal',
      'carvajalg63': 'Gonzo Gonzalez',
      's': 'test test'
    };
    
    if (emailMappings[emailPart]) {
      return emailMappings[emailPart];
    }
    
    // Para otros emails, capitalizar y limpiar
    return emailPart
      .replace(/[._-]/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Manejar cambio en el campo de cliente con autocompletado
  const handleClientChange = (e) => {
    const value = e.target.value;
    setClientSearchTerm(value);
    setFormData(prev => ({
      ...prev,
      nombre_cliente: value
    }));
    
    // Mostrar dropdown solo si hay texto y clientes filtrados
    if (value.trim()) {
      const filtered = clients.filter(client =>
        client.name.toLowerCase().includes(value.toLowerCase()) ||
        (client.specialty && client.specialty.toLowerCase().includes(value.toLowerCase()))
      );
      setFilteredClients(filtered.slice(0, 10)); // Limitar a 10 resultados
      setShowClientDropdown(filtered.length > 0);
    } else {
      setFilteredClients([]);
      setShowClientDropdown(false);
    }
    
    // Limpiar error del campo
    if (errors.nombre_cliente) {
      setErrors(prev => ({
        ...prev,
        nombre_cliente: ''
      }));
    }
  };

  // Seleccionar cliente del dropdown
  const handleClientSelect = (client) => {
    setFormData(prev => ({
      ...prev,
      nombre_cliente: client.name
    }));
    setClientSearchTerm(client.name);
    setShowClientDropdown(false);
    
    // Limpiar error si existe
    if (errors.nombre_cliente) {
      setErrors(prev => ({
        ...prev,
        nombre_cliente: ''
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">
                {record ? '✏️ Editar Registro' : '➕ Nuevo Registro'}
              </h2>
              <p className="text-purple-100 mt-1">
                {record ? 'Modificar información del cliente sin contacto Meta' : 'Agregar nuevo cliente sin contacto Meta'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Fila 1: Mes, Seguimiento, Fecha */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Mes *
                </label>
                <select
                  name="mes"
                  value={formData.mes}
                  onChange={handleChange}
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 ${
                    errors.mes ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="">Seleccionar mes...</option>
                  {mesesOptions.map(mes => (
                    <option key={mes} value={mes}>{mes}</option>
                  ))}
                </select>
                {errors.mes && <p className="text-red-500 text-sm mt-1">{errors.mes}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Seguimiento *
                </label>
                {loadingUsers ? (
                  <div className="w-full p-3 border-2 border-gray-300 rounded-xl flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-500 mr-2"></div>
                    <span className="text-gray-500">Cargando agentes...</span>
                  </div>
                ) : (
                  <select
                    name="seguimiento"
                    value={formData.seguimiento}
                    onChange={handleChange}
                    className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 ${
                      errors.seguimiento ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  >
                    <option value="">Seleccionar agente</option>
                    {users.map(user => (
                      <option key={user.email} value={getDisplayName(user)}>
                        {getDisplayName(user)} ({user.role || 'standard'})
                      </option>
                    ))}
                  </select>
                )}
                {errors.seguimiento && <p className="text-red-500 text-sm mt-1">{errors.seguimiento}</p>}
                {!loadingUsers && users.length === 0 && (
                  <p className="text-amber-600 text-sm mt-1">
                    No hay agentes disponibles. Asegúrate de tener usuarios registrados en el sistema.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Fecha del Asunto *
                </label>
                <input
                  type="date"
                  name="fecha_asunto"
                  value={formData.fecha_asunto}
                  onChange={handleChange}
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 ${
                    errors.fecha_asunto ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.fecha_asunto && <p className="text-red-500 text-sm mt-1">{errors.fecha_asunto}</p>}
              </div>
            </div>

            {/* Fila 2: Cliente, Cuenta, Célula */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="relative">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nombre del Cliente *
                </label>
                <input
                  type="text"
                  name="nombre_cliente"
                  value={clientSearchTerm}
                  onChange={handleClientChange}
                  onFocus={() => setShowClientDropdown(clientSearchTerm.length > 0)}
                  onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                  placeholder="Buscar cliente existente..."
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 ${
                    errors.nombre_cliente ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                
                {/* Dropdown de clientes */}
                {showClientDropdown && filteredClients.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredClients.map((client) => (
                      <div
                        key={client.id}
                        onClick={() => handleClientSelect(client)}
                        className="p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">{client.name}</div>
                        {client.specialty && (
                          <div className="text-sm text-gray-500">{client.specialty}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {errors.nombre_cliente && <p className="text-red-500 text-sm mt-1">{errors.nombre_cliente}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Link Cuenta Publicitaria *
                </label>
                <input
                  type="url"
                  name="cuenta_publicitaria"
                  value={formData.cuenta_publicitaria}
                  onChange={handleChange}
                  placeholder="https://business.facebook.com/..."
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 ${
                    errors.cuenta_publicitaria ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.cuenta_publicitaria && <p className="text-red-500 text-sm mt-1">{errors.cuenta_publicitaria}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Célula *
                </label>
                <select
                  name="celula"
                  value={formData.celula}
                  onChange={handleChange}
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 ${
                    errors.celula ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="">Seleccionar célula...</option>
                  {celulasOptions.map(celula => (
                    <option key={celula} value={celula}>{celula}</option>
                  ))}
                </select>
                {errors.celula && <p className="text-red-500 text-sm mt-1">{errors.celula}</p>}
              </div>
            </div>

            {/* Fila 3: Campaña y Estado */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Campaña *
                </label>
                <input
                  type="text"
                  name="campana"
                  value={formData.campana}
                  onChange={handleChange}
                  placeholder="Nombre de la campaña"
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 ${
                    errors.campana ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.campana && <p className="text-red-500 text-sm mt-1">{errors.campana}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Estado
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Fila 4: Asunto */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Asunto *
              </label>
              <textarea
                name="asunto"
                value={formData.asunto}
                onChange={handleChange}
                placeholder="Descripción detallada del problema de contacto Meta..."
                rows="3"
                className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 ${
                  errors.asunto ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors.asunto && <p className="text-red-500 text-sm mt-1">{errors.asunto}</p>}
            </div>

            {/* Fila 5: Acciones */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Acciones Realizadas
              </label>
              <textarea
                name="acciones"
                value={formData.acciones}
                onChange={handleChange}
                placeholder="Describe las acciones tomadas para resolver el problema de contacto Meta..."
                rows="3"
                className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
              />
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 font-medium transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Guardando...
              </div>
            ) : (
              record ? '💾 Actualizar' : '➕ Crear Registro'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MetaNoContactForm;
